import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import { Download, Pencil, Trash2 } from 'lucide-react';
import { useSubagent, useDeleteSubagent } from '@/features/subagents/hooks';
import { downloadSubagentApi } from '@/features/subagents/api';
import { useAuth } from '@/features/auth/AuthContext';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { getApiErrorMessage } from '@/lib/api-client';

export default function SubagentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const query = useSubagent(id);
  const remove = useDeleteSubagent();
  const [downloading, setDownloading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (query.isLoading)
    return (
      <div className="flex justify-center py-12 text-text-muted">
        <Spinner size={24} />
      </div>
    );
  if (query.isError || !query.data) {
    return <p className="text-red-400">Subagente não encontrado.</p>;
  }

  const s = query.data;
  const canEdit = user?.role === 'admin' || s.uploader.id === user?.id;
  const content = s.systemPrompt ?? s.contentMd ?? '';

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const { blob, filename } = await downloadSubagentApi(s.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao baixar o subagente.'));
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(s.id);
      toast.success('Subagente excluído.');
      nav('/subagentes');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Falha ao excluir.'));
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {s.color && (
                  <span
                    className="inline-block h-3 w-3 rounded-full border border-border"
                    style={{ backgroundColor: s.color }}
                    aria-hidden="true"
                  />
                )}
                <h1 className="text-2xl font-semibold">{s.name}</h1>
                <Badge tone="brand">v{s.version}</Badge>
              </div>
              <p className="text-text-secondary max-w-2xl">{s.description}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {s.model && <Badge tone="info">{s.model}</Badge>}
                {s.tools.map((t) => (
                  <Badge key={t} tone="warn">
                    {t}
                  </Badge>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {s.tags.map((t) => (
                  <Badge key={t.id} tone="neutral">
                    #{t.name}
                  </Badge>
                ))}
              </div>
              <p className="mt-3 text-xs text-text-muted">
                Enviado por {s.uploader.displayName || s.uploader.email} •{' '}
                {new Date(s.createdAt).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleDownload} loading={downloading}>
                <Download size={16} /> Baixar .md
              </Button>
              {canEdit && (
                <>
                  <Button variant="secondary" onClick={() => nav(`/subagentes/${s.id}/editar`)}>
                    <Pencil size={16} /> Editar
                  </Button>
                  <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                    <Trash2 size={16} /> Excluir
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-6">
          {content && (
            <section>
              <h2 className="text-lg font-semibold mb-2">System prompt</h2>
              <div
                className="prose-skill text-text-secondary"
                dangerouslySetInnerHTML={{ __html: marked.parse(content) as string }}
              />
            </section>
          )}
        </CardBody>
      </Card>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Excluir subagente"
        size="sm"
      >
        <p className="text-sm text-text-secondary">
          Confirma excluir o subagente <span className="font-medium text-text-primary">{s.name}</span>?
          Essa ação não pode ser desfeita.
        </p>
        <div className="mt-5 flex gap-3 justify-end">
          <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={remove.isPending}>
            <Trash2 size={16} /> Excluir
          </Button>
        </div>
      </Modal>
    </div>
  );
}
